import { useEffect, useState } from 'react';

const DEFAULT_INTERVAL_MS = 5 * 60 * 1000;

export interface VersionCheckResult {
  /** True when the server reports a build different from the one running. */
  updateAvailable: boolean;
  /** Version string reported by the server, if known. */
  latestVersion: string | null;
  /** Version string compiled into this bundle. */
  currentVersion: string;
}

/**
 * Polls /version.json (emitted at build time) and reports when a newer
 * frontend build has been deployed. Disabled in dev.
 */
export function useVersionCheck(intervalMs = DEFAULT_INTERVAL_MS): VersionCheckResult {
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  useEffect(() => {
    if (import.meta.env.DEV) return;
    let cancelled = false;

    const check = async () => {
      try {
        const res = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = (await res.json()) as { version?: string };
        if (!cancelled && typeof data.version === 'string' && data.version) {
          setLatestVersion(data.version);
        }
      } catch {
        // Network hiccups are not worth surfacing; try again next tick.
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };

    void check();
    const timer = window.setInterval(check, intervalMs);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [intervalMs]);

  return {
    updateAvailable: latestVersion !== null && latestVersion !== __APP_VERSION__,
    latestVersion,
    currentVersion: __APP_VERSION__,
  };
}
